import React from "react"
import { Table } from "react-bootstrap"
import { useIntl } from "react-intl"
import PropTypes from "prop-types"
import { formatXAxisTick, formatYAxisTick } from "../utils/formatters"

/**
 * HistoricalDataTable component
 * 
 * Provides a data table alternative to the chart visualization for accessibility.
 * Displays all historical exchange rate data points in a tabular format with
 * formatted timestamps and values.
 * 
 * This component ensures users who cannot interact with the visual chart
 * (e.g., screen reader users) can still access all the data.
 * 
 * Requirements: 10.6
 */
const HistoricalDataTable = ({ data, currency, quoteCurrency, timeRange }) => {
  const intl = useIntl()

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    return formatXAxisTick(timestamp, timeRange, intl.locale)
  }

  // Format currency value for display
  const formatValue = (value) => {
    return formatYAxisTick(value, quoteCurrency, intl.locale)
  }

  return (
    <div className="historical-data-table mt-4">
      <h3 className="h5 mb-3">
        {intl.formatMessage({ id: "Historical Data Table" })}
      </h3>
      <Table striped bordered hover responsive size="sm">
        <thead className="thead-dark">
          <tr>
            <th scope="col">
              {intl.formatMessage({ id: "Time" })}
            </th>
            <th scope="col">
              {intl.formatMessage(
                { id: "Exchange Rate ({currency}/{quote})" },
                { currency, quote: quoteCurrency }
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="2" className="text-center text-muted">
                {intl.formatMessage({ id: "No data available" })}
              </td>
            </tr>
          ) : (
            data.map((point, index) => (
              <tr key={`${point.timestamp}-${index}`}>
                <td>{formatTimestamp(point.timestamp)}</td>
                <td>{formatValue(point.value)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  )
}

HistoricalDataTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.number.isRequired,
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  currency: PropTypes.string.isRequired,
  quoteCurrency: PropTypes.string.isRequired,
  timeRange: PropTypes.string.isRequired,
}

export default HistoricalDataTable
